// On-Track Technologies — a work item's own screen, as a pop-up.
//
// The client asked for the screen to open "as a pop-up" rather than in a new
// tab, so this is the product's IssueScreenPanel — the same screen Folders
// and the Kanban board open, every field editable — over the page the person
// was already reading, so they keep their place.
//
// It renders into document.body through a portal: the cards on these pages
// clip their overflow, and a dialog that a card can clip is not a dialog.
// Escape closes it, so does the backdrop and the ×. So does the shell moving
// to another tab: the page behind stays mounted (hidden tabs keep running),
// and a portal into document.body would otherwise stay over the new tab —
// which is why "Zebra Search" and "View workflow" inside the screen seemed
// to do nothing.
//
// Two things Grids & Gantts' own pop-up has, for a page that asks for them
// (the others keep this pop-up exactly as it was):
//  - `fillable`: ⤢ fills the window (nearly edge to edge) for a work item
//    with a lot on its screen, ⤡ brings it back — remembered in this browser
//    under Grids & Gantts' own key, so both open the same way.
//  - `stepInto`: a key clicked inside the work item (a sub-task, a link, its
//    parent) opens HERE, over the same page; ← Back steps out again.

import { Component, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import IssueScreenPanel from '@modules/database/work-items/frontend/components/IssueScreenPanel';
import { FONT } from '../high-level-finance/util';

class PanelBoundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) return <div style={{ padding: 16, fontSize: 13, color: '#dc2626' }}>The work-item screen failed to render: {String((this.state.err && this.state.err.message) || this.state.err)}</div>;
    return this.props.children;
  }
}

const MAX_KEY = 'stride.workItemPopup.max';
const readMax = () => { try { return localStorage.getItem(MAX_KEY) === '1'; } catch { return false; } };

export default function WorkItemPopup({
  issue, onClose, ui, narrow, title, onOpenTab, onChanged,
  jiraFields = null, fieldNameMap = null,
  // Optional: the page's own skin class / a measuring ref on the scroll body,
  // and the creation tree for creating children from the screen.
  bodyClassName, bodyRef, creationTree = null,
  fillable = false, stepInto = false,
}) {
  const [max, setMax] = useState(readMax);
  const toggleMax = () => setMax((m) => { try { localStorage.setItem(MAX_KEY, m ? '0' : '1'); } catch { /* a private window keeps nothing */ } return !m; });
  const big = fillable && max && !narrow;
  // the keys stepped into from this work item (stepInto); a new item from the page starts afresh
  const [trail, setTrail] = useState([]);
  useEffect(() => { setTrail([]); }, [issue?.key]);
  const location = useLocation();
  const openedAt = useRef(location.pathname + location.search);
  useEffect(() => { if (issue) openedAt.current = location.pathname + location.search; }, [issue]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (issue && location.pathname + location.search !== openedAt.current) onClose();
  }, [location.pathname, location.search, issue, onClose]);
  useEffect(() => {
    if (!issue) return undefined;
    // Escape belongs to a product modal open over this one (transition screen…).
    const onKey = (e) => { if (e.key === 'Escape' && !document.querySelector('.modal-overlay')) onClose(); };
    window.addEventListener('keydown', onKey);
    // The page behind a dialog must not scroll under it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [issue, onClose]);

  if (!issue || typeof document === 'undefined') return null;
  const stepped = stepInto && trail.length > 0;
  const key = stepped ? trail[trail.length - 1] : issue.key;
  const shownIssue = stepped ? { key } : issue;
  const backTo = stepped ? (trail.length > 1 ? trail[trail.length - 2] : issue.key) : null;
  const pill = { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${ui.border}`, background: ui.card, color: ui.muted, fontFamily: FONT };

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={`${key} — work item`}
      onClick={onClose}
      // zIndex 950: above the shell's tab bar (300) and the pages' own full-
      // screen views (900), BELOW the product's modals (.modal-overlay, 1000)
      // — the transition screen, create and bulk dialogs the work-item screen
      // opens must land on top of it, not behind it.
      style={{
        position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(9, 15, 26, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: narrow ? 0 : (big ? 12 : 24), boxSizing: 'border-box', fontFamily: FONT,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: ui.card, color: ui.fg, border: narrow ? 'none' : `1px solid ${ui.border}`,
          borderRadius: narrow ? 0 : 14, boxShadow: '0 24px 64px rgba(0,0,0,.35)',
          // A phone gets the whole screen; the header row below never scrolls.
          // (fillable: Grids & Gantts' sizes — wider, a work item's screen has
          // line-item tables beside its fields; ⤢ the whole window)
          width: narrow || big ? '100%' : (fillable ? 'min(1440px, 100%)' : 'min(1100px, 100%)'),
          height: narrow || big ? '100%' : undefined, maxHeight: narrow || big ? '100%' : '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: narrow ? '10px 10px 10px 14px' : '10px 14px', borderBottom: `1px solid ${ui.border}`, flex: '0 0 auto', background: ui.card }}>
          {stepped ? <button onClick={() => setTrail((t) => t.slice(0, -1))} title={`Back to ${backTo}`} style={{ ...pill, flex: '0 0 auto' }}>← {backTo}</button> : null}
          <div style={{ minWidth: 0, flex: '1 1 auto', display: 'flex', alignItems: narrow ? 'flex-start' : 'center', flexDirection: narrow ? 'column' : 'row', gap: narrow ? 2 : 10 }}>
            <strong style={{ fontSize: 13.5 }}>{key}</strong>
            {title && !stepped ? <span style={{ fontSize: narrow ? 14 : 12.5, fontWeight: narrow ? 700 : 400, color: narrow ? ui.fg : ui.muted, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, maxWidth: '100%', ...(narrow ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } : { whiteSpace: 'nowrap' }) }}>{title}</span> : null}
          </div>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center', flex: '0 0 auto' }}>
            {onOpenTab && !narrow ? (
              <button onClick={() => onOpenTab(key)} title="Open this work item in its own tab" style={pill}>↗ Open in a tab</button>
            ) : null}
            {fillable && !narrow ? (
              <button onClick={toggleMax} aria-label={big ? 'Back to the normal size' : 'Fill the window'} title={big ? 'Back to the normal size' : 'Fill the window'} aria-pressed={big}
                style={{ background: 'none', border: 'none', color: ui.muted, fontSize: 17, lineHeight: 1, cursor: 'pointer', padding: '0 4px', fontFamily: FONT }}>{big ? '⤡' : '⤢'}</button>
            ) : null}
            <button onClick={onClose} aria-label="Close" title="Close (Esc)"
              style={{ background: 'none', border: 'none', color: ui.muted, fontSize: narrow ? 28 : 22, lineHeight: 1, cursor: 'pointer', padding: narrow ? '0 8px' : '0 4px', minWidth: narrow ? 44 : undefined, minHeight: narrow ? 44 : undefined, fontFamily: FONT }}>×</button>
          </span>
        </div>
        <div ref={bodyRef} className={bodyClassName} style={{ overflowY: 'auto', padding: narrow ? '0 10px 12px' : '0 16px 14px', flex: '1 1 auto' }}>
          <PanelBoundary>
            <IssueScreenPanel
              key={key} issueKey={key} issueData={shownIssue}
              {...(stepInto ? { onOpenIssue: (k) => setTrail((t) => [...t, k]) } : {})}
              jiraFields={jiraFields || undefined} fieldNameMap={fieldNameMap || undefined}
              creationTree={creationTree} siteUrl={null}
              onFieldUpdate={(k, fields) => onChanged && onChanged(k, fields)}
              onParentChange={(k, parent) => onChanged && onChanged(k, { parent })}
              /* `fields` is null when the screen changed the SHAPE of the
                 tree rather than a value; the third argument says how, so a
                 page can take one row out rather than reload everything. */
              onIssueCreated={() => onChanged && onChanged(key, null, 'created')}
              onIssueDeleted={() => { if (onChanged) onChanged(key, null, 'deleted'); if (stepped) setTrail((t) => t.slice(0, -1)); else onClose(); }}
              onClose={onClose} />
          </PanelBoundary>
        </div>
      </div>
    </div>,
    document.body,
  );
}
